import { useState } from 'react';
import styled from 'styled-components';

import { Text } from '../../cdl/Text/Text';
import { translate } from '../../common/helpers/translate.helper';
import { IconArrowNarrowRight } from '../../common/icons/cdl/ArrowNarrowRight';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';
import type { DemandCategorySummary } from '../types/DemandCategorySummary';
import type { DemandProductCategory } from '../types/DemandProductCategory';

import { DemandHistoricalChart } from './DemandHistoricalChart';
import { DemandVolume } from './DemandVolume';

const CategoryValue = styled(Text)`
    transition: transform 250ms cubic-bezier(0.22, 1, 0.36, 1);

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

const CategoryArrow = styled(IconArrowNarrowRight)`
    position: absolute;
    top: 50%;
    right: ${theme.space[8]};
    opacity: 0;
    transform: translate(8px, -50%);
    pointer-events: none;
    transition:
        opacity 150ms ease-out,
        transform 250ms cubic-bezier(0.22, 1, 0.36, 1);

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

const CategoryRow = styled.button`
    font-feature-settings: inherit;
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 104px;
    align-items: center;
    gap: ${theme.space[12]};
    width: 100%;
    min-height: 36px;
    padding: ${theme.space[6]} ${theme.space[8]};
    border: 0;
    border-radius: ${theme.radii[6]};
    background: transparent;
    color: ${theme.colors.foreground.onEmphasis};
    cursor: pointer;
    text-align: left;
    transition: background-color 220ms ease-in-out;

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }

    &:hover,
    &:focus-visible,
    &[data-hovered='true'] {
        background: ${theme.colors.foreground.muted};
    }

    &:hover ${CategoryArrow}, &:focus-visible ${CategoryArrow}, &[data-hovered='true'] ${CategoryArrow} {
        opacity: 1;
        transform: translate(0, -50%);
    }

    &:hover ${CategoryValue}, &:focus-visible ${CategoryValue}, &[data-hovered='true'] ${CategoryValue} {
        transform: translateX(-24px);
    }
`;

interface DemandHistoricalTotalCardProps {
    categories: DemandCategorySummary[];
    categoryLabels: Map<DemandProductCategory, string>;
    totalVolume: number;
    selectedCategory?: DemandProductCategory;
    onSelectCategory: (category: DemandProductCategory) => void;
}

export const DemandHistoricalTotalCard = ({
    categories,
    categoryLabels,
    totalVolume,
    selectedCategory,
    onSelectCategory,
}: DemandHistoricalTotalCardProps) => {
    const [hoveredCategory, setHoveredCategory] = useState<DemandProductCategory>();
    const sortedCategories = [...categories].sort((first, second) => second.volume - first.volume);

    return (
        <Box
            padding={16}
            display="flex"
            flexDirection="column"
            gap={12}
            backgroundColor="background.emphasis"
            color="foreground.onEmphasis"
            borderRadius={8}
        >
            <Box
                display="flex"
                alignItems="baseline"
                justifyContent="space-between"
                gap={16}
                paddingRight={8}
                flexWrap="wrap"
            >
                <Text variant="subtitle1" color="foreground.onEmphasis">
                    {translate('demandDefinition.totalDemand')}
                </Text>
                <Text variant="subtitle1" color="foreground.onEmphasis" marginLeft="auto">
                    <DemandVolume volume={totalVolume} />
                </Text>
            </Box>
            <Box display="flex" alignItems="center" gap={16} flexWrap="wrap">
                <DemandHistoricalChart
                    categories={sortedCategories}
                    categoryLabels={categoryLabels}
                    totalVolume={totalVolume}
                    selectedCategory={selectedCategory}
                    hoveredCategory={hoveredCategory}
                    onHoverCategory={setHoveredCategory}
                    onSelectCategory={onSelectCategory}
                />
                <Box flex="1 1 270px" minWidth={0} display="flex" flexDirection="column" gap={2}>
                    {sortedCategories.map((category) => (
                        <CategoryRow
                            key={category.category}
                            type="button"
                            data-hovered={hoveredCategory === category.category}
                            aria-pressed={selectedCategory === category.category}
                            onMouseEnter={() => setHoveredCategory(category.category)}
                            onMouseLeave={() => setHoveredCategory(undefined)}
                            onFocus={() => setHoveredCategory(category.category)}
                            onBlur={() => setHoveredCategory(undefined)}
                            onClick={() => onSelectCategory(category.category)}
                        >
                            <Text variant="small" color="foreground.onEmphasis">
                                {categoryLabels.get(category.category) ?? category.category}
                            </Text>
                            <CategoryValue variant="small" color="foreground.onEmphasis" textAlign="right">
                                <DemandVolume volume={category.volume} />
                            </CategoryValue>
                            <CategoryArrow width={16} height={16} aria-hidden="true" />
                        </CategoryRow>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};
